import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input, message, Table, InputNumber, AutoComplete } from 'antd';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import productService from '../../service/product.service';

const StyledButton = styled(Button)`
  margin-top: 16px;
  background-color: #012443;
  &:hover {
    background-color: #023d70ff !important;
  }
`;

export function parsePricingNumericValue(value) {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const normalized = String(value).trim().replace(/\./g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

export function normalizePricingProductRow(row = {}) {
  const amount = Number(row.amount) || 0;
  const priceList = Number(row.priceList) || 0;
  const explicitTotal = parsePricingNumericValue(row.total);
  const computedTotal =
    amount > 0 && priceList > 0
      ? Number((amount * priceList).toFixed(2))
      : 0;
  const total =
    Number.isFinite(explicitTotal) && explicitTotal >= 0
      ? explicitTotal
      : computedTotal;

  return {
    ...row,
    total,
    netPrice: total,
  };
}

export function normalizePricingProducts(products = []) {
  if (!Array.isArray(products)) return [];
  return products.map(normalizePricingProductRow);
}

export function sumPricingProductsTotal(products = []) {
  return normalizePricingProducts(products).reduce(
    (sum, prod) => sum + (Number(prod.total) || 0),
    0,
  );
}

export function syncPricingProductsWithFreightValue(
  products = [],
  valueFreightage,
) {
  const freightValue = parsePricingNumericValue(valueFreightage);
  const normalized = normalizePricingProducts(products);

  if (!Number.isFinite(freightValue) || freightValue < 0) {
    return normalized;
  }

  const currentSum = sumPricingProductsTotal(normalized);
  if (Math.abs(currentSum - freightValue) < 0.005) {
    return normalized;
  }

  const hasContent = (row) =>
    String(row?.nameProducts || "").trim() !== "" ||
    Number(row?.amount) > 0 ||
    Number(row?.priceList) > 0 ||
    Number(row?.total) > 0;

  const filledRows = normalized.filter(hasContent);

  if (filledRows.length === 0) {
    return [
      {
        nameProducts: "Frete",
        amount: 1,
        priceList: freightValue,
        total: freightValue,
        netPrice: freightValue,
      },
    ];
  }

  if (filledRows.length === 1) {
    const row = filledRows[0];

    return [
      {
        ...row,
        amount: 1,
        priceList: freightValue,
        total: freightValue,
        netPrice: freightValue,
      },
    ];
  }

  const targetIdx = normalized.findIndex(hasContent);
  if (targetIdx === -1) return normalized;

  const othersSum = normalized.reduce(
    (sum, row, index) =>
      index === targetIdx ? sum : sum + (Number(row.total) || 0),
    0,
  );
  const targetTotal = Number((freightValue - othersSum).toFixed(2));
  const targetRow = normalized[targetIdx];
  const amount = Number(targetRow.amount) || 1;
  const priceList = Number((targetTotal / amount).toFixed(2));

  return normalizePricingProducts(
    normalized.map((row, index) =>
      index === targetIdx
        ? {
            ...row,
            priceList,
            total: targetTotal,
            netPrice: targetTotal,
          }
        : row,
    ),
  );
}

export function extractSimulationFreightValue(simulationData = {}) {
  const freightData = simulationData?.freight || {};
  const candidates = [
    freightData?.valueFreightage,
    simulationData?.valueFreightage,
    freightData?.value,
    simulationData?.value,
    freightData?.total,
    simulationData?.total,
  ];

  for (const candidate of candidates) {
    const parsed = parsePricingNumericValue(candidate);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }

  return undefined;
}

const ProductTable = ({
  product,
  onChange,
  disabled = false,
  unitPricePrecision = 2,
  generalTotalOverride,
}) => {

    const [products, setProducts] = useState([
        createProducts(),
    ]);
    const [productsData, setProductsData] = useState([]);

    const navigate = useNavigate();

    // Formatação para exibição (totais, etc.) sempre com 2 casas decimais
    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') return '';

        let numberValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numberValue)) return '';

        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numberValue);
    };

    const roundUnitPrice = (value) => {
        if (value === '' || value === null || value === undefined) return value;
        const factor = 10 ** unitPricePrecision;
        return Math.round(Number(value) * factor) / factor;
    };

    // Formatação do valor unitário (apenas número; R$ fica no prefixo do input)
    const formatUnitPriceInput = (value) => {
        if (value === null || value === undefined || value === '') return '';

        const stringValue = value.toString();
        const [integerPart, decimalPart = ''] = stringValue.split('.');
        const integerWithDots = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const paddedDecimals = decimalPart
            .slice(0, unitPricePrecision)
            .padEnd(unitPricePrecision, '0');

        return `${integerWithDots},${paddedDecimals}`;
    };

    const parseUnitPrice = (value) => {
        if (value === null || value === undefined || value === '') return '';
        if (typeof value === 'number') return value;

        return value.replace(/\./g, '').replace(',', '.');
    };

    const calculateTotals = (product) => {
        const total = Number(
            ((product.amount || 0) * (product.priceList || 0)).toFixed(2),
        );
        return {
            ...product,
            total,
            netPrice: total,
        };
    };

    useEffect(() => {
        if (product && product.length > 0) {
            setProducts(normalizePricingProducts(product));
        }
    }, [product]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const snapshot = await productService.getProduct();
                setProductsData(snapshot);
            } catch (error) {
                message.error("Erro ao carregar os produtos. Tente novamente.");
                console.error(error);
            }
        };
        fetchProducts();
    }, []);

    function createProducts() {
        return {
            nameProducts: "",
            amount: "",
            priceList: "",
            total: "",
            netPrice: "",
        };
    };

    const productOptions = useMemo(() => [
        ...productsData.map(prod => ({
            value: prod.product,
        }))
    ], [productsData]);

    function handleChangeProducts(index, field, value) {
        const updatedProducts = [...products];
        const normalizedValue =
            field === 'priceList' ? roundUnitPrice(value) : value;
        updatedProducts[index][field] = normalizedValue;
        if (field === 'amount' || field === 'priceList') {
            updatedProducts[index] = calculateTotals(updatedProducts[index]);
        }
        setProducts(updatedProducts);
        onChange && onChange(updatedProducts);
    };

    function handleAddProduct() {
        const newProducts = [...products, createProducts()];
        setProducts(newProducts);
        onChange && onChange(newProducts);
    };

    function handleRemoveProduct(index) {
        const updatedProducts = [...products];
        updatedProducts.splice(index, 1);
        setProducts(updatedProducts);
        onChange && onChange(updatedProducts);
    };

    const displayedGeneralTotal = (() => {
        const overrideValue = parsePricingNumericValue(generalTotalOverride);
        if (Number.isFinite(overrideValue)) {
            return overrideValue;
        }
        return sumPricingProductsTotal(products);
    })();

    return (
        <div>
            <Table pagination={false} dataSource={products} rowKey={(record, index) => index}>
                <Table.Column
                    title="Nome do Produto"
                    dataIndex="nameProducts"
                    key="nameProducts"
                    render={(text, record, index) => (
                        <AutoComplete
                        style={{ width: '100%' }}
                        options={productOptions}
                        value={record.nameProducts}
                        placeholder="Digite o nome do produto"
                        disabled={disabled}
                        filterOption={(inputValue, option) =>
                            option.value.toUpperCase().includes(inputValue.toUpperCase())
                        }
                        onChange={(value) =>
                            handleChangeProducts(index, 'nameProducts', value)
                        }
                        >
                        <Input disabled={disabled} />
                        </AutoComplete>
                    )}
                />
                <Table.Column title="Quantidade" dataIndex="amount" key="amount"
                    render={(text, record, index) => (
                        <InputNumber
                            min={0}
                            precision={0}
                            value={record.amount}
                            disabled={disabled}
                            onChange={(value) => handleChangeProducts(index, 'amount', value)}
                        />
                    )}
                />
                <Table.Column title="Valor Unitário" dataIndex="priceList" key="priceList"
                    render={(text, record, index) => (
                        <InputNumber
                            min={0}
                            precision={unitPricePrecision}
                            step={0.01}
                            controls={false}
                            prefix="R$"
                            formatter={formatUnitPriceInput}
                            parser={parseUnitPrice}
                            value={record.priceList}
                            disabled={disabled}
                            style={{ width: '40%' }}
                            onChange={(value) => handleChangeProducts(index, 'priceList', value)}
                        />
                    )}
                />
                <Table.Column title="Valor Total" dataIndex="total" key="total"
                    render={(text, record, index) => (
                        <span>{formatCurrency(record.total)}</span>
                    )}
                />
                <Table.Column title="Ações" key="actions"
                    render={(text, record, index) => (
                        <Button
                            type="danger"
                            onClick={() => handleRemoveProduct(index)}
                            disabled={disabled || products.length === 1}
                            style={{ color: "red" }}
                        >
                            <DeleteOutlinedIcon />
                        </Button>
                    )}
                />
            </Table>
            {!disabled && (
                <StyledButton type="primary" onClick={handleAddProduct}>
                    Adicionar Produto
                </StyledButton>
            )}
            <span style={{marginLeft: "62%", marginTop: '16px', fontWeight: 'bold' }}>
                Total Geral: {formatCurrency(displayedGeneralTotal)}
            </span>
        </div>
    );

};

export default ProductTable;